import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAccount } from '../../contexts/AccountContext';
import { apiErrorMessage } from '../../utils/apiError';
import mcpServiceService from '../../services/mcpServiceService';
import ProductSupportTab from '../support/ProductSupportTab';
import Button from '../shared/Button';
import Toggle from '../shared/Toggle';
import McpContentTab from './McpContentTab';
import McpTermsTab from './McpTermsTab';
import McpHotelSender from './McpHotelSender';
import McpHotelImplementation from './McpHotelImplementation';
import { GROUPS, HOTEL_DEFAULTS, HotelFieldGroup, toPayload } from './McpHotelFields';
import './McpServicePage.css';

// ONE HOTEL'S MCP AS A SERVICE — /mcp-service/hotels/:hotelId, opened from the list of hotels running it (McpServicePage),
// the same list → detail shape as the other products. Its tabs:
//
//   Implementation  the pipelines made for the hotel's PMS and this hotel's implementation of each, drawn on the
//                 pipeline designer's board (McpHotelImplementation) — first, and where the page opens
//   Settings      the service (credential, live / paused), booking policy, OPERA codes, guest emails, content — one card
//                 per group (McpHotelFields, shared with the set-up wizard), one Save
//   Assistants    the approved clients and what each may do
//   Email sender  the hotel's own sending domain, overriding the chain's (McpHotelSender)
//   Terms · Usage as before, for this hotel only
//   Content       review, publish and crawl the hotel's content (McpContentTab); its HCONNECT MCP Backend view shows
//                 what the backend actually holds for the hotel (McpBackendTab) — publish, then see it arrive
//
// The data is the chain console's (GET /accounts/{accountId}) for the chain in the navbar. A hotel that is not in it is
// WARNED about, not redirected: it is a confusing view, not a breach — the API answers only what the caller may reach.

const TABS = [['implementation', 'Implementation'], ['settings', 'Settings'],
  ['assistants', 'Assistants'], ['sender', 'Email sender'], ['terms', 'Terms'], ['content', 'Content'], ['usage', 'Usage'], ['support', 'Support']];

export default function McpHotelPage() {
  const { hotelId } = useParams();
  const navigate = useNavigate();
  const { selectedAccount } = useAccount();
  const [searchParams, setSearchParams] = useSearchParams();
  // ?tab=backend was its own tab; it is now a view of Content, so old links land there.
  const asked = searchParams.get('tab') === 'backend' ? 'content' : searchParams.get('tab');
  const tab = TABS.some(([k]) => k === asked) ? asked : 'implementation';
  const setTab = (k) => setSearchParams((prev) => { const p = new URLSearchParams(prev); p.set('tab', k); return p; }, { replace: true });
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    if (!selectedAccount) return;
    setError(null);
    try { setData(await mcpServiceService.getAccount(selectedAccount)); } catch (e) { setError(apiErrorMessage(e)); }
  }, [selectedAccount]);
  useEffect(() => { setData(null); load(); }, [load]);

  const act = async (what, fn) => {
    setBusy(what);
    setError(null);
    try { setData(await fn()); } catch (e) { setError(apiErrorMessage(e)); } finally { setBusy(null); }
  };

  const hotel = data ? data.hotels.find(h => String(h.hotelId) === String(hotelId)) : null;

  return (
    <div className="mcps-page">
      <div className="mcps-head">
        <div>
          <p className="mcps-meta"><Link to="/mcp-service">MCP as a Service</Link> ›</p>
          <h1>{hotel ? hotel.hotelName : 'Hotel'}
            {hotel && hotel.settings && <span className={`mcps-pill ${hotel.settings.service_status === 'ACTIVE' ? 'mcps-pill-verified' : 'mcps-pill-pending'}`}>
              {hotel.settings.service_status === 'ACTIVE' ? 'Live' : 'Paused'}</span>}
          </h1>
          {hotel && hotel.city && <p className="mcps-sub">{hotel.city}</p>}
        </div>
      </div>

      {!selectedAccount && <div className="os-msg">Choose a chain in the navigation bar.</div>}
      {error && <div className="os-msg os-msg-error">{error}</div>}
      {data && !hotel && (
        <div className="os-msg">This hotel is not in the chain chosen in the navigation bar, or you cannot reach it. Choose its chain,
          or go back to <Link to="/mcp-service">the list</Link>.</div>
      )}
      {hotel && !hotel.settings && (
        <div className="os-msg">MCP as a Service is not set up for this hotel yet. <Button variant="primary" onClick={() => navigate(`/mcp-service/new?hotelId=${hotel.hotelId}`)}>Set it up</Button></div>
      )}

      {hotel && hotel.settings && (
        <>
          <div className="mcps-tabs">
            {TABS.map(([k, label]) => (
              <button key={k} type="button" className={`mcps-tab ${tab === k ? 'active' : ''}`} onClick={() => setTab(k)}>{label}</button>
            ))}
          </div>
          {tab === 'implementation' && <McpHotelImplementation hotel={hotel} />}
          {tab === 'settings' && <SettingsTab hotel={hotel} sources={data.sources} busy={busy} act={act} />}
          {tab === 'assistants' && <AssistantsTab hotel={hotel} busy={busy} act={act} />}
          {tab === 'sender' && <McpHotelSender hotelId={hotel.hotelId} />}
          {tab === 'terms' && <McpTermsTab hotels={[hotel]} />}
          {tab === 'content' && <McpContentTab hotels={[hotel]} initialView={searchParams.get('tab') === 'backend' ? 'backend' : 'review'} />}
          {tab === 'usage' && <UsageTab hotels={[hotel]} />}
          {/* An MCP service is one hotel's, so the hotel id is the instance (support.js PRODUCT_OWNERS). */}
          {tab === 'support' && <ProductSupportTab product="MCP" productId={hotelId} noun="MCP as a Service at this hotel"
            context={{ screen: 'mcp-service-hotel', hotelId }} />}
        </>
      )}
      {selectedAccount && !data && !error && <p>Loading…</p>}
    </div>
  );
}

// ── Settings ─────────────────────────────────────────────────────────────────

function SettingsTab({ hotel, sources, busy, act }) {
  const [form, setForm] = useState({ ...HOTEL_DEFAULTS, ...(hotel.settings || {}) });
  useEffect(() => { setForm({ ...HOTEL_DEFAULTS, ...(hotel.settings || {}) }); }, [hotel]);
  const edit = hotel.canEdit;
  const key = `hotel-${hotel.hotelId}`;

  return (
    <div className="mcps-section">
      {GROUPS.map(g => (
        <div className="mcps-card" key={g.key}>
          <div className="mcps-card-head">
            <h3>{g.title}</h3>
            {g.key === 'service' && (
              <Toggle on={form.service_status === 'ACTIVE'} onLabel="Live" offLabel="Paused" disabled={!edit}
                      onChange={(on) => setForm(f => ({ ...f, service_status: on ? 'ACTIVE' : 'PAUSED' }))} />
            )}
          </div>
          <p className="mcps-meta">{g.hint}{g.key === 'service' ? ' Paused refuses every assistant at this hotel, straight away.' : ''}</p>
          <HotelFieldGroup group={g.key} form={form} setForm={setForm} edit={edit} hotel={hotel} sources={sources} />
        </div>
      ))}
      {edit
        ? <Button variant="primary" disabled={!!busy} onClick={() => act(key, () => mcpServiceService.saveHotel(hotel.hotelId, toPayload(form)))}>
            {busy === key ? 'Saving…' : 'Save'}</Button>
        : <p className="mcps-meta">A hotel admin can change these settings.</p>}
    </div>
  );
}

// ── Assistants ───────────────────────────────────────────────────────────────

function AssistantsTab({ hotel, busy, act }) {
  const edit = hotel.canEdit;
  return (
    <div className="mcps-card">
      <div className="mcps-card-head"><h3>Approved assistants</h3></div>
      <p className="mcps-meta">An assistant is approved by issuing it an MCP credential for this hotel (Credentials › MCP). Suspending one
        signs it out at once.</p>
      {hotel.clients.length ? (
        <table className="mcps-table">
          <thead><tr><th>Name</th><th>Instance</th><th>May</th><th>Status</th></tr></thead>
          <tbody>{hotel.clients.map(cl => (
            <tr key={cl.credentialId}>
              <td>{cl.name}</td>
              <td className="mcps-mono">{cl.instanceId || '—'}</td>
              <td><select value={cl.scope} disabled={!edit || !!busy}
                          onChange={e => act(`client-${cl.credentialId}`, () => mcpServiceService.saveClient(cl.credentialId, { scope: e.target.value }))}>
                <option value="read">Search and read</option><option value="booking">Also book and cancel</option></select></td>
              <td><Toggle size="sm" on={cl.status === 'ACTIVE'} onLabel="Active" offLabel="Suspended" disabled={!edit} busy={busy === `client-${cl.credentialId}`}
                          onChange={(on) => act(`client-${cl.credentialId}`, () => mcpServiceService.saveClient(cl.credentialId, { status: on ? 'ACTIVE' : 'SUSPENDED' }))} /></td>
            </tr>))}</tbody>
        </table>
      ) : <p className="mcps-meta">None yet.</p>}
    </div>
  );
}

// ── Usage ────────────────────────────────────────────────────────────────────

function UsageTab({ hotels }) {
  const [hotelId, setHotelId] = useState(hotels[0] ? hotels[0].hotelId : null);
  const [days, setDays] = useState(30);
  const [u, setU] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!hotelId) return;
    setU(null); setError(null);
    mcpServiceService.getUsage(hotelId, days).then(setU).catch(e => setError(apiErrorMessage(e)));
  }, [hotelId, days]);
  const rows = (o) => Object.entries(o || {}).sort((a, b) => b[1] - a[1]);

  return (
    <div className="mcps-section">
      <div className="mcps-grid">
        {hotels.length > 1 && <label>Hotel<select value={hotelId || ''} onChange={e => setHotelId(e.target.value)}>
          {hotels.map(h => <option key={h.hotelId} value={h.hotelId}>{h.hotelName}</option>)}</select></label>}
        <label>Period<select value={days} onChange={e => setDays(Number(e.target.value))}>
          {[7, 30, 90, 365].map(d => <option key={d} value={d}>Last {d} days</option>)}</select></label>
      </div>
      {error && <div className="os-msg os-msg-error">{error}</div>}
      {u && (
        <>
          <div className="mcps-stats">
            <div><div className="mcps-meta">Tool calls</div><div className="mcps-big">{u.total}</div></div>
            <div><div className="mcps-meta">Bookings started</div><div className="mcps-big">{u.bookings}</div></div>
            <div><div className="mcps-meta">Refused / failed</div><div className="mcps-big">{(u.byOutcome || {}).error || 0}</div></div>
          </div>
          <div className="mcps-cols">
            <table className="mcps-table"><thead><tr><th>Tool</th><th>Calls</th></tr></thead>
              <tbody>{rows(u.byTool).map(([k, v]) => <tr key={k}><td>{k}</td><td>{v}</td></tr>)}</tbody></table>
            <table className="mcps-table"><thead><tr><th>Assistant</th><th>Calls</th></tr></thead>
              <tbody>{rows(u.byClient).map(([k, v]) => <tr key={k}><td className="mcps-mono">{k}</td><td>{v}</td></tr>)}</tbody></table>
            {u.channels && <table className="mcps-table"><thead><tr><th>Assistant</th><th>Bookings</th><th>Confirmed</th><th>Cancelled</th><th>Revenue</th><th>Conversion</th></tr></thead>
              <tbody>{u.channels.map(c => {
                const calls = (u.byClient || {})[c.clientId] || (u.byClient || {})[`client:${c.clientId}`] || 0;
                const confirmed = (c.byStatus.confirmed || 0) + (c.byStatus.cancelled || 0);
                return <tr key={c.clientId}><td className="mcps-mono">{c.clientId}</td><td>{c.bookings}</td><td>{c.byStatus.confirmed || 0}</td>
                  <td>{c.byStatus.cancelled || 0}</td><td>{c.revenue.map(r => `${r.total.toFixed(2)} ${r.currency}`).join(', ') || '—'}</td>
                  <td>{calls ? `${Math.round((confirmed / calls) * 1000) / 10}% of ${calls} calls` : '—'}</td></tr>;
              })}</tbody></table>}
            <table className="mcps-table"><thead><tr><th>Day</th><th>Calls</th></tr></thead>
              <tbody>{Object.entries(u.byDay || {}).sort().reverse().map(([k, v]) => <tr key={k}><td>{k}</td><td>{v}</td></tr>)}</tbody></table>
          </div>
        </>
      )}
    </div>
  );
}
